import { Ban, Calendar, User } from "lucide-react";
import { useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "components/ui/button";
import {
  useGetDepositDraftDetailQuery,
  useUndoDepositBatchMutation,
} from "@/services/deposit-reconciliation";
import { toast } from "@/components/ui/use-toast";
import { formatToBRL } from "@/utils/formatToMoney";
import { formatAndConvertTimezoneDateTime } from "@/utils/format-date";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import type { IApiError } from "src/@types/deposit-reconciliation";

import { MESSAGES, TEXTS } from "../../../utils/messages";
import { BATCH_STATUS } from "../../../constants/status";
import { BatchStatusBadge } from "./status-badge";
import UndoModal from "./undo-modal";

interface BatchDetailModalProps {
  batchId: number | null;
  onClose: () => void;
  onChanged?: () => void;
}

export default function BatchDetailModal({
  batchId,
  onClose,
  onChanged,
}: BatchDetailModalProps) {
  const [undoOpen, setUndoOpen] = useState(false);

  const { data: detail, isFetching } = useGetDepositDraftDetailQuery(
    batchId ?? 0,
    { skip: batchId === null },
  );

  const [undoBatch, { isLoading: undoing }] = useUndoDepositBatchMutation();

  const isActive = detail?.status === BATCH_STATUS.ACTIVE;
  const isUndone = detail?.status === BATCH_STATUS.UNDONE;

  const handleConfirmUndo = async (reason: string) => {
    if (!detail) return;
    try {
      await undoBatch({ batchId: detail.id, reason }).unwrap();
      toast(MESSAGES.BATCH.UNDONE(detail.id));
      setUndoOpen(false);
      onChanged?.();
      onClose();
    } catch (err) {
      toast(MESSAGES.BATCH.UNDO_ERROR(err as IApiError));
    }
  };

  return (
    <>
      <Dialog open={batchId !== null} onOpenChange={open => !open && onClose()}>
        <DialogContent
          className="max-w-xl max-h-[85vh] overflow-y-auto"
          onInteractOutside={event => {
            if (undoOpen) event.preventDefault();
          }}
          onEscapeKeyDown={event => {
            if (undoOpen) event.preventDefault();
          }}
        >
          <DialogHeader>
            <DialogTitle className="flex flex-wrap items-center gap-3">
              {detail ? (
                <>
                  <span className="whitespace-nowrap">
                    {TEXTS.BATCH.TITLE(detail.id)}
                  </span>
                  <BatchStatusBadge status={detail.status} />
                </>
              ) : (
                TEXTS.COMMON.LOADING
              )}
            </DialogTitle>
            <DialogDescription>
              {detail
                ? TEXTS.BATCH.SUMMARY(
                    detail.storeCode,
                    formatToBRL(detail.totalAmount),
                  )
                : ""}
            </DialogDescription>
          </DialogHeader>

          {isFetching && !detail ? (
            <div className="py-10 text-center text-sm text-slate-500">
              {TEXTS.COMMON.LOADING}
            </div>
          ) : detail ? (
            <div className="space-y-4">
              {isUndone && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-xs space-y-1">
                  <div className="font-semibold text-rose-900 flex items-center gap-1">
                    <Ban className="w-3.5 h-3.5" />
                    {TEXTS.BATCH.UNDONE_LABEL}
                  </div>
                  <div className="text-rose-800">
                    <div>
                      <strong>{TEXTS.BATCH.UNDONE_BY}</strong>{" "}
                      {detail.undoneByName ?? detail.undoneByEmail}
                    </div>
                    <div>
                      <strong>{TEXTS.BATCH.UNDONE_AT}</strong>{" "}
                      {formatAndConvertTimezoneDateTime(detail.undoneAt, { fallback: "—" })}
                    </div>
                    <div>
                      <strong>{TEXTS.BATCH.UNDONE_REASON}</strong>{" "}
                      {detail.undoneReason}
                    </div>
                  </div>
                </div>
              )}

              <div className="text-xs text-slate-600 space-y-1 border border-slate-200 rounded p-3">
                <div className="flex items-center gap-2">
                  <User className="w-3.5 h-3.5" />
                  <strong>{TEXTS.BATCH.CREATED_BY}</strong>{" "}
                  {detail.createdByName ?? detail.createdByEmail}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" />
                  <strong>{TEXTS.BATCH.COMMITTED_AT}</strong>{" "}
                  {formatAndConvertTimezoneDateTime(detail.committedAt ?? detail.createdAt, { fallback: "—" })}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" />
                  <strong>{TEXTS.BATCH.PERIOD}</strong>{" "}
                  {formatDateToDMY(detail.periodStart)} a{" "}
                  {formatDateToDMY(detail.periodEnd)}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-slate-700 mb-1">
                  {TEXTS.BATCH.NSU_SECTION(detail.nsus.length)}
                </h4>
                <div className="border border-slate-200 rounded text-xs overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-2 py-1">
                          {TEXTS.BATCH.COLUMN_NUMBER}
                        </th>
                        <th className="text-right px-2 py-1">
                          {TEXTS.BATCH.COLUMN_AMOUNT}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.nsus.map(n => (
                        <tr key={n.id} className="border-t border-slate-100">
                          <td className="px-2 py-1 font-mono">{n.number}</td>
                          <td className="px-2 py-1 text-right">
                            {formatToBRL(
                              n.adjustmentAmount !== 0
                                ? n.adjustmentAmount
                                : n.amount,
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-slate-700 mb-1">
                  {TEXTS.BATCH.DEPOSIT_SECTION(detail.deposits.length)}
                </h4>
                <div className="border border-slate-200 rounded text-xs overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-2 py-1">
                          {TEXTS.BATCH.COLUMN_BANK}
                        </th>
                        <th className="text-left px-2 py-1">
                          {TEXTS.BATCH.COLUMN_DATE}
                        </th>
                        <th className="text-right px-2 py-1">
                          {TEXTS.BATCH.COLUMN_AMOUNT}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.deposits.map(d => (
                        <tr key={d.id} className="border-t border-slate-100">
                          <td className="px-2 py-1">{d.bankName}</td>
                          <td className="px-2 py-1">
                            {formatDateToDMY(d.depositDate)}
                          </td>
                          <td className="px-2 py-1 text-right">
                            {formatToBRL(d.amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="pt-3 border-t border-slate-200">
                {isActive ? (
                  <Button
                    onClick={() => setUndoOpen(true)}
                    className="w-full bg-[#2B0C1A] hover:bg-[#4B303C] text-white"
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    {TEXTS.BATCH.UNDO_ACTION}
                  </Button>
                ) : (
                  <Button disabled className="w-full" variant="outline">
                    {TEXTS.BATCH.ALREADY_UNDONE}
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {detail && (
        <UndoModal
          open={undoOpen}
          batch={detail}
          isLoading={undoing}
          onCancel={() => setUndoOpen(false)}
          onConfirm={handleConfirmUndo}
        />
      )}
    </>
  );
}
